import { Box, Center, Group, Loader, Stack, Text } from '@mantine/core';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DS_AI_MOTION,
	dsAiMoveMs,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { stageGrowIn } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import { PROCESSING_ICON_SIZE } from './ProcessingCard.styles';
import type { ProcessingReaderStatusView } from './ProcessingCard.types';

interface Props {
	status: ProcessingReaderStatusView;
}

/** Grows in below the last stage, like a stage does, so the centred card eases instead of snapping. */
const STAGE_MOTION = `${dsAiMoveMs(DS_AI_MOTION.baseMs)} ${DS_AI_MOTION.easeOut}`;

/** The reader's state under the checklist ("Spinning up GPUs…"), with a running substep's spinner. */
export const ProcessingReaderStatus = ({ status }: Props) => {
	const tCapManage = useTranslationCapManage();

	return (
		<ReaderStatusReveal
			display="grid"
			role="status"
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_READER_STATUS}
		>
			<Group
				align="flex-start"
				gap={10}
				wrap="nowrap"
				mih={0}
				pt={7}
				px={8}
				pb={0}
				style={{ overflow: 'hidden' }}
			>
				<Center component="span" inline w={PROCESSING_ICON_SIZE} h="1.25rem" flex="none">
					<Loader type="oval" size={16} color="blue.6" />
				</Center>
				<Stack component="span" gap={0} align="stretch" justify="flex-start">
					<Text span size="md" c="gray.9">
						{tCapManage(status.messageKey)}
					</Text>
					<Text span fz="sm" c="dark.0">
						{tCapManage(status.hintKey)}
					</Text>
				</Stack>
			</Group>
		</ReaderStatusReveal>
	);
};

/** The reader status, which grows open as it arrives. */
const ReaderStatusReveal = styled(Box)<StyledMantineProps>({
	gridTemplateRows: '1fr',
	animation: `${stageGrowIn} ${STAGE_MOTION} both`,
});
